/**
 * FaseAllegatiModal - Gestione documenti con versionamento per una fase di commessa.
 *
 * Funzionalità:
 * - Lista documenti raggruppati per gruppo_id (un "documento" con N revisioni)
 * - Espansione storico revisioni
 * - Upload nuovo documento (versione 1)
 * - Upload nuova revisione di documento esistente
 * - Download di qualsiasi revisione
 * - Elimina singola revisione
 */

import { useState, useEffect, useRef } from 'react'
import {
  X, Upload, Download, Trash2, ChevronDown, ChevronRight,
  FileText, Loader2, Plus, History, Paperclip
} from 'lucide-react'
import api from '../../core/services/api'

interface AllegatoItem {
  id: string
  titolo: string
  filename: string
  content_type: string
  file_size: number
  created_at: string
  versione: number
  gruppo_id: string | null
  note?: string | null
}

interface DocumentoGruppo {
  gruppo_id: string
  titolo: string
  versione_corrente: AllegatoItem
  revisioni: AllegatoItem[]  // tutte le versioni, newest first
  expanded: boolean
}

interface Props {
  faseId: string
  faseTitolo: string
  onClose: () => void
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function fileIcon(contentType: string): string {
  if (contentType.includes('pdf')) return '📄'
  if (contentType.includes('word') || contentType.includes('wordprocessing')) return '📝'
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊'
  if (contentType.includes('image')) return '🖼️'
  if (contentType.includes('zip')) return '🗜️'
  return '📎'
}

export default function FaseAllegatiModal({ faseId, faseTitolo, onClose }: Props) {
  const [gruppi, setGruppi] = useState<DocumentoGruppo[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadTarget, setUploadTarget] = useState<string | null>(null)  // gruppo_id se revisione
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [uploadTitolo, setUploadTitolo] = useState('')
  const [uploadNote, setUploadNote] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadAllegati()
  }, [faseId])

  const loadAllegati = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/v1/allegati/fase_commessa/${faseId}`)
      const items: AllegatoItem[] = res.data

      // Raggruppa per gruppo_id
      const map = new Map<string, AllegatoItem[]>()
      for (const item of items) {
        const key = item.gruppo_id || item.id
        if (!map.has(key)) map.set(key, [])
        map.get(key)!.push(item)
      }

      const gruppiList: DocumentoGruppo[] = []
      map.forEach((revisioni, gruppo_id) => {
        // Già ordinate per versione desc dal backend
        gruppiList.push({
          gruppo_id,
          titolo: revisioni[0].titolo,
          versione_corrente: revisioni[0],
          revisioni,
          expanded: false,
        })
      })

      setGruppi(gruppiList)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile || !uploadTitolo.trim()) return
    setUploading(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', selectedFile)
      form.append('titolo', uploadTitolo.trim())
      if (uploadNote.trim()) form.append('note', uploadNote.trim())
      if (uploadTarget) form.append('gruppo_id', uploadTarget)

      await api.post(`/v1/allegati/fase_commessa/${faseId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      setShowUploadForm(false)
      setUploadTarget(null)
      setUploadTitolo('')
      setUploadNote('')
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      await loadAllegati()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Errore nel caricamento file')
    } finally {
      setUploading(false)
    }
  }

  const handleStartRevision = (gruppo: DocumentoGruppo) => {
    setUploadTarget(gruppo.gruppo_id)
    setUploadTitolo(gruppo.titolo)
    setUploadNote('')
    setSelectedFile(null)
    setShowUploadForm(true)
  }

  const handleStartNewDoc = () => {
    setUploadTarget(null)
    setUploadTitolo('')
    setUploadNote('')
    setSelectedFile(null)
    setShowUploadForm(true)
  }

  const handleDownload = (allegato: AllegatoItem) => {
    window.open(`/api/v1/allegati/download/${allegato.id}`, '_blank')
  }

  const handleDelete = async (allegatoId: string) => {
    if (!confirm('Eliminare questa revisione?')) return
    try {
      await api.delete(`/v1/allegati/${allegatoId}`)
      await loadAllegati()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Errore eliminazione')
    }
  }

  const toggleExpand = (gruppo_id: string) => {
    setGruppi(prev => prev.map(g =>
      g.gruppo_id === gruppo_id ? { ...g, expanded: !g.expanded } : g
    ))
  }

  const handleCancelUpload = () => {
    setShowUploadForm(false)
    setUploadTarget(null)
    setUploadTitolo('')
    setUploadNote('')
    setSelectedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-primary-600" />
            <div>
              <h3 className="font-semibold text-gray-900">Documenti fase</h3>
              <p className="text-xs text-gray-500">{faseTitolo}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-3 px-3 py-2 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            </div>
          ) : gruppi.length === 0 && !showUploadForm ? (
            <div className="text-center py-10 text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Nessun documento allegato</p>
              <p className="text-xs mt-1">Carica il primo documento con il pulsante qui sotto</p>
            </div>
          ) : (
            <div className="space-y-2">
              {gruppi.map((gruppo) => (
                <div key={gruppo.gruppo_id} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Documento (versione corrente) */}
                  <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                    <span className="text-lg">{fileIcon(gruppo.versione_corrente.content_type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900 truncate">{gruppo.titolo}</span>
                        <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-medium">
                          v{gruppo.versione_corrente.versione}
                        </span>
                        {gruppo.revisioni.length > 1 && (
                          <button
                            onClick={() => toggleExpand(gruppo.gruppo_id)}
                            className="flex items-center gap-0.5 text-xs text-gray-500 hover:text-gray-700"
                          >
                            <History className="w-3 h-3" />
                            {gruppo.revisioni.length} rev.
                            {gruppo.expanded
                              ? <ChevronDown className="w-3 h-3" />
                              : <ChevronRight className="w-3 h-3" />}
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">
                        {gruppo.versione_corrente.filename} · {formatBytes(gruppo.versione_corrente.file_size)} · {formatDate(gruppo.versione_corrente.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleDownload(gruppo.versione_corrente)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 rounded transition-colors"
                        title="Scarica"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleStartRevision(gruppo)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
                        title="Nuova revisione"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(gruppo.versione_corrente.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                        title="Elimina questa versione"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Storico revisioni espanso */}
                  {gruppo.expanded && gruppo.revisioni.length > 1 && (
                    <div className="border-t border-gray-100 bg-gray-50">
                      {gruppo.revisioni.slice(1).map((rev) => (
                        <div key={rev.id} className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 last:border-0">
                          <span className="text-xs text-gray-400 w-8 text-center">v{rev.versione}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-600 truncate">{rev.filename}</p>
                            <p className="text-xs text-gray-400">{formatBytes(rev.file_size)} · {formatDate(rev.created_at)}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDownload(rev)}
                              className="p-1 text-gray-400 hover:text-primary-600 rounded"
                              title="Scarica questa versione"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(rev.id)}
                              className="p-1 text-gray-400 hover:text-red-600 rounded"
                              title="Elimina questa versione"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Form upload */}
          {showUploadForm && (
            <div className="mt-4 border border-primary-200 rounded-lg p-4 bg-primary-50">
              <h4 className="text-sm font-medium text-gray-800 mb-3">
                {uploadTarget ? 'Carica nuova revisione' : 'Nuovo documento'}
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Titolo *</label>
                  <input
                    type="text"
                    value={uploadTitolo}
                    onChange={e => setUploadTitolo(e.target.value)}
                    placeholder="Es. Relazione tecnica, Schema elettrico..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Note</label>
                  <input
                    type="text"
                    value={uploadNote}
                    onChange={e => setUploadNote(e.target.value)}
                    placeholder="Opzionale"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">File *</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={e => setSelectedFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-primary-600 file:text-white cursor-pointer"
                  />
                  {selectedFile && (
                    <p className="text-xs text-gray-500 mt-1">{selectedFile.name} · {formatBytes(selectedFile.size)}</p>
                  )}
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleUpload}
                    disabled={uploading || !selectedFile || !uploadTitolo.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg disabled:opacity-50 hover:bg-primary-700 transition-colors"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? 'Caricamento...' : 'Carica'}
                  </button>
                  <button
                    onClick={handleCancelUpload}
                    className="px-4 py-2 border border-gray-200 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-500">
            {gruppi.length} {gruppi.length === 1 ? 'documento' : 'documenti'}
          </span>
          {!showUploadForm && (
            <button
              onClick={handleStartNewDoc}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nuovo documento
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
